import { useCallback } from "react"
import { createContext, useContext, ReactNode } from "react"
import { PaymentMethod } from "../types"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchPaymentMethods } from "../queries"
import { AlertMessage, Loading } from "source/shared/components"

type PaymentMethodsContextType = {
  paymentMethods: PaymentMethod[]
  refreshPaymentMethods: () => void
}

const PaymentMethodsContext = createContext<
  PaymentMethodsContextType | undefined
>(undefined)

export const PaymentMethodsProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const queryClient = useQueryClient()

  const {
    data: paymentMethods = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["PaymentMethods"],
    queryFn: () => fetchPaymentMethods(),
  })

  const refreshPaymentMethods = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["PaymentMethods"] })
  }, [queryClient])

  if (isError) {
    return (
      <AlertMessage type="error" icon="general#exclamation-triangle">
        Something went wrong. Please try again.
      </AlertMessage>
    )
  }

  if (isLoading) {
    return <Loading message="Loading" />
  }

  return (
    <PaymentMethodsContext.Provider
      value={{
        paymentMethods,
        refreshPaymentMethods,
      }}
    >
      {children}
    </PaymentMethodsContext.Provider>
  )
}

export const usePaymentMethodsContext = () => {
  const context = useContext(PaymentMethodsContext)
  if (!context) {
    throw new Error(
      "usePaymentMethodsContext must be used within a PaymentMethodsProvider",
    )
  }
  return context
}
